import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRightIcon, CameraIcon, CheckIcon, MapPinIcon, QrCodeIcon, ScanLineIcon, ShieldCheckIcon, XIcon } from 'lucide-react';
const ease = [0.23, 1, 0.32, 1] as const;
type Mode = 'choix' | 'scan' | 'scan-resultat' | 'signalement' | 'transmis';
interface FoundPetDialogProps {
  open: boolean;
  onClose: () => void;
}
export function FoundPetDialog({
  open,
  onClose
}: FoundPetDialogProps) {
  const [mode, setMode] = useState<Mode>('choix');
  const [photo, setPhoto] = useState(false);
  const [place, setPlace] = useState('');
  const close = () => {
    onClose();
    window.setTimeout(() => {
      setMode('choix');
      setPhoto(false);
      setPlace('');
    }, 250);
  };
  return <AnimatePresence data-id="element-70" data-mp="2rexmk4fjr6uj">
      {open && <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-71" data-mp="2ri1yxp3618wa">
          <button type="button" aria-label="Fermer" onClick={close} className="absolute inset-0 bg-ville-navy/40" data-id="element-72" data-mp="2whntpj01kgmx" />
          <motion.div role="dialog" aria-modal="true" aria-label="Animal trouvé" initial={{
        y: 20,
        opacity: 0,
        scale: 0.98
      }} animate={{
        y: 0,
        opacity: 1,
        scale: 1
      }} exit={{
        y: 12,
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease
      }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-73" data-mp="3ah5k22szk1ye">
            <button type="button" onClick={close} aria-label="Fermer" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-74" data-mp="3p5tf87yx69nt">
              <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-75" data-mp="2zsuh5avoxy5z" />
            </button>

            <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-76" data-mp="34syu4qxe4arg">Animal trouvé</h2>

            <AnimatePresence mode="wait" data-id="element-77" data-mp="39jyvq9hgfh0a">
              <motion.div key={mode} initial={{
            opacity: 0,
            x: 16
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -16
          }} transition={{
            duration: 0.22,
            ease
          }} className="mt-5" data-id="element-78" data-mp="39kqegjpydk95">
                {mode === 'choix' && <div data-id="element-79" data-mp="3si1cy23rg9nj">
                    <p className="text-[15px] text-slate-600" data-id="element-80" data-mp="3sijixqg8628n">
                      Merci d’aider. Deux façons de retrouver son gardien.
                    </p>

                    <div className="mt-4 space-y-3" data-id="element-81" data-mp="1y4a61actg3vs">
                      <button type="button" onClick={() => setMode('scan')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-82" data-mp="1y51rhscp8l8c">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-ville-indigo-soft text-ville-indigo" data-id="element-83" data-mp="1bxfubpf9e9zv">
                          <QrCodeIcon className="h-5 w-5" aria-hidden="true" data-id="element-84" data-mp="24kiqwc0dkanh" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-85" data-mp="1zl5sqv41zh5l">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-86" data-mp="1zjmbp2akp22r">Scanner son collier</span>
                          <span className="block text-sm text-slate-500" data-id="element-87" data-mp="1vlwylp53ph9i">
                            L’animal porte une médaille avec un code
                          </span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-88" data-mp="k71n2c1r3vx6" />
                      </button>

                      <button type="button" onClick={() => setMode('signalement')} className="flex w-full items-center gap-4 rounded-2xl border border-slate-200 p-5 text-left transition-colors duration-150 ease-ville hover:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-89" data-mp="2csd3wojzqt36">
                        <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-slate-100 text-ville-navy" data-id="element-90" data-mp="2vpwhm5he8zvx">
                          <CameraIcon className="h-5 w-5" aria-hidden="true" data-id="element-91" data-mp="2hqws6vm24gyp" />
                        </span>
                        <span className="min-w-0 flex-1" data-id="element-92" data-mp="2luus38j7hvst">
                          <span className="block text-[15px] font-bold text-ville-navy" data-id="element-93" data-mp="16aybq6ojusng">Signaler à la Ville</span>
                          <span className="block text-sm text-slate-500" data-id="element-94" data-mp="1aee6sykz0ky1">Aucune médaille ou code illisible</span>
                        </span>
                        <ArrowRightIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-95" data-mp="xv0ruad3hbxj" />
                      </button>
                    </div>
                  </div>}

                {mode === 'scan' && <div data-id="element-96" data-mp="17u83mt46kyij">
                    <div className="relative grid h-44 place-items-center overflow-hidden rounded-2xl bg-slate-100" data-id="element-97" data-mp="17tq2bi2nvu3r">
                      <ScanLineIcon className="h-12 w-12 text-slate-400" aria-hidden="true" data-id="element-98" data-mp="k1hqwhogeb77" />
                      <motion.span className="absolute inset-x-8 h-0.5 bg-ville-indigo" initial={{
                  top: '20%'
                }} animate={{
                  top: ['20%', '80%', '20%']
                }} transition={{
                  duration: 2.4,
                  repeat: Infinity,
                  ease: 'linear'
                }} data-id="element-99" data-mp="f2eol4zuyj2x" />
                    </div>

                    <ol className="mt-5 space-y-3" data-id="element-100" data-mp="3sick15fg8h83">
                      {['Approchez doucement l’animal, sans le brusquer.', 'Repérez la médaille sur son collier.', 'Cadrez le code QR de la médaille dans la zone ci-dessus.'].map((etape, index) => <li key={etape} className="flex gap-3 text-[15px] text-slate-600" data-id="element-101" data-mp="ztk8klirwf3u" data-mpi={index}>
                          <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-ville-indigo-soft text-xs font-bold text-ville-indigo" data-id="element-102" data-mp="zvehf1grsgrk">
                            {index + 1}
                          </span>
                          {etape}
                        </li>)}
                    </ol>

                    <button type="button" onClick={() => setMode('scan-resultat')} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-103" data-mp="h3c9cei728sx">
                      Simuler le scan de la médaille
                    </button>
                    <button type="button" onClick={() => setMode('signalement')} className="mt-3 w-full text-sm font-semibold text-ville-indigo hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-104" data-mp="339f6uth8dpcy">
                      Le code est illisible — signaler à la Ville
                    </button>
                  </div>}

                {mode === 'scan-resultat' && <div className="text-center" data-id="element-105" data-mp="2fdt7hpv3vzsh">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-106" data-mp="ik98rtufylwp">
                      <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-107" data-mp="dkwl56vqxat2" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-108" data-mp="3im81htyhs2do">Médaille reconnue</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-109" data-mp="3njrrts8gs5lz">
                      Cet animal est enregistré à Brossard. Son gardien vient d’être avisé qu’il a été trouvé. Vos
                      coordonnées ne lui sont pas transmises : la Ville fait le lien.
                    </p>
                    <p className="mt-3 rounded-2xl bg-slate-50 px-5 py-3 text-sm text-slate-600" data-id="element-110" data-mp="2ztdx1tyhvsnp">
                      Médaille BRO-•••21 · avis envoyé à l’instant
                    </p>
                    <button type="button" onClick={close} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-111" data-mp="2qvlgkmh1eeyq">
                      Terminer
                    </button>
                  </div>}

                {mode === 'signalement' && <div data-id="element-112" data-mp="2nbtels7ipnhh">
                    <p className="text-[15px] text-slate-600" data-id="element-113" data-mp="2nd4qkfb4a1jo">
                      Une photo et un lieu suffisent. Votre signalement crée une requête au service concerné.
                    </p>

                    <button type="button" onClick={() => setPhoto(true)} className={`mt-4 flex w-full items-center justify-center gap-2 rounded-2xl border px-5 py-4 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${photo ? 'border-ville-indigo bg-ville-indigo-soft text-ville-indigo' : 'border-dashed border-slate-300 text-ville-navy hover:border-ville-indigo'}`} data-id="element-114" data-mp="2x97t3m9vs275">
                      {photo ? <CheckIcon className="h-5 w-5" data-id="element-115" data-mp="21j0q3c6d7fn2" /> : <CameraIcon className="h-5 w-5" data-id="element-116" data-mp="1wlin02f13umz" />}
                      {photo ? 'Photo jointe' : 'Prendre une photo de l’animal'}
                    </button>

                    <div className="mt-4" data-id="element-117" data-mp="1sm9ut1zn04tj">
                      <label htmlFor="lieu-trouve" className="block text-sm font-bold text-ville-navy" data-id="element-118" data-mp="1nm3vr0r1fg1f">
                        Où l’avez-vous trouvé ?
                      </label>
                      <div className="mt-1.5 flex items-center gap-2 rounded-xl border border-slate-200 px-4 focus-within:border-ville-indigo" data-id="element-119" data-mp="2g9ho0ljsjnf7">
                        <MapPinIcon className="h-5 w-5 shrink-0 text-slate-400" aria-hidden="true" data-id="element-120" data-mp="2bhq7l3ucmy2s" />
                        <input id="lieu-trouve" type="text" value={place} onChange={event => setPlace(event.target.value)} placeholder="Ma position actuelle ou une adresse" className="w-full border-0 bg-transparent py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:outline-none" data-id="element-121" data-mp="26jom6c9gamo5" />
                      </div>
                    </div>

                    <button type="button" onClick={() => setMode('transmis')} disabled={!photo} className="mt-6 w-full rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-122" data-mp="3kjt8jef4ubil">
                      Envoyer à la Ville
                    </button>
                  </div>}

                {mode === 'transmis' && <div className="text-center" data-id="element-123" data-mp="2d67auwuyxo12">
                    <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-124" data-mp="2d70kq6p84tnf">
                      <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-125" data-mp="292j58jpwd2ig" />
                    </span>
                    <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-126" data-mp="2my3iayiy2qhy">
                      Signalement transmis
                    </h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-127" data-mp="2wsvhg6s6dleu">
                      Votre signalement a été transmis au service animalier. Vous pouvez en suivre l’avancement dans
                      Mes demandes.
                    </p>
                    <p className="mt-3 font-mono text-sm text-slate-500" data-id="element-128" data-mp="30w1ohgn43gmd">#REQ-2903</p>
                    <Link to="/mes-demandes/req-2903" onClick={close} className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-129" data-mp="1bgvhsobf5uj2">
                      Suivre ma demande
                      <ArrowRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-130" data-mp="3dee2it7orjiv" />
                    </Link>
                  </div>}
              </motion.div>
            </AnimatePresence>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}